'use client';
// Custom app file
// This is the wrapper component that will wrap every page of the application
// https://nextjs.org/docs/advanced-features/custom-app
import React, { useEffect, useState } from 'react';
import reportWebVitals from '@/reportWebVitals';
import Head from 'next/head';
import Honeybadger from '@honeybadger-io/js';
import { HoneybadgerErrorBoundary } from '@honeybadger-io/react';
import useMixpanel from '@/hooks/useMixpanel';
import '@/assets/stylesheets/components/skeleton.scss';
import '@/assets/stylesheets/layout/_global.scss';
import '@/assets/stylesheets/solution_overrides.scss';
import { solutionFromHost } from '@/utils/server';
import Keys from '@/constants/Keys';
import t from '@/utils/Language/UI-translations';
import { LanguageProvider } from '@/context/language.context';
import { ServerPropsContext } from '@/context/server-props.context';
import AppCrashFallback from '@/components/AppCrashFallback/AppCrashFallback';

// On dev, we don't want to call Honeybadger.configure because it overrides console logging to reference HB source instead of our own source
// Ref: https://github.com/honeybadger-io/honeybadger-js/issues/397#issuecomment-665284205
const hbConfig = {
  apiKey: process.env.HONEYBADGER_API_KEY,
  environment: process.env.NODE_ENV
  // reportData: true // uncomment to enable reporting for dev environment
};
if (process.env.NODE_ENV !== 'development' || hbConfig.reportData) {
  Honeybadger.configure(hbConfig);
}

function MyApp({ Component, pageProps }) {
  const { track } = useMixpanel();
  const [solution, setSolution] = useState(null);
  const [tracked, setTracked] = useState(false);
  const { favicon, page_title } = solution?.ui?.meta ?? {};
  const defaultDescription = t('One Degree makes it easy to find food, housing, job training, after-school programs, and much more_');

  useEffect(() => {
    // delay the title and favicon until we're on the client-side
    setSolution(solutionFromHost(window.location.hostname));
  }, []);

  useEffect(() => {
    if (solution && !tracked) {
      track('user session');
      setTracked(true);
    }
  }, [solution]);

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  // TODO: check if this is still relevant
  reportWebVitals();

  const getLayout = Component.getLayout || ((page) => page);

  return (
    <HoneybadgerErrorBoundary honeybadger={Honeybadger} ErrorComponent={AppCrashFallback}>
      <LanguageProvider>
        <ServerPropsContext.Provider value={pageProps}>
          <Head>
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link rel="shortcut icon" href={favicon} type="image/png" />
            {<title>{pageProps.pageTitle || page_title}</title>}
            {/* Site-wide SEO defaults, matching the Angular layout (application.html.erb).
                Pages can override these tags by rendering tags with the same keys (see SeoMetadata). */}
            <meta key="description" name="description" content={defaultDescription} />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="One Degree" />
            <meta key="og:url" property="og:url" content={Keys.odas.host} />
            <meta key="og:title" property="og:title" content={pageProps.pageTitle || page_title || 'One Degree'} />
            <meta key="og:description" property="og:description" content={defaultDescription} />
            <meta key="og:image" property="og:image" content={Keys.odas.defaultImage} />
          </Head>
          {getLayout(<Component {...pageProps} />)}
        </ServerPropsContext.Provider>
      </LanguageProvider>
    </HoneybadgerErrorBoundary>
  );
}

export default MyApp;
